<template>
    <div class="card checkin-card position-relative">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="Guest Check-In"
                @onClickCloseButton="goBack"
            />
        </div>

        <div class="px-2 mt-2" v-if="reservation">
            <div class="row">
                <div class="col-12 col-md-7 mb-2">
                    <section class="section-card mb-2">
                        <div class="section-card-header d-flex align-items-center gap-2">
                            <h4 class="mb-0">Guest Details</h4>
                            <span v-if="reservation.reservation_no" class="badge bg-light-primary">Reservation No: {{ reservation.reservation_no }}</span>
                        </div>
                        <div class="section-card-body">
                            <div class="guest-card">
                                <GuestSummary :guest="primaryGuest"/>
                            </div>
                            <div class="d-flex gap-3 checkin-dates mt-2">
                                <span><span class="summary-label">Date of Check-in</span> {{ reservation.check_in }}</span>
                                <span><span class="summary-label">Date of Check-out</span> {{ reservation.check_out }}</span>
                            </div>
                            <div class="booking-info mt-2">
                                <div class="row g-1">
                                    <div class="col-12 col-sm-6">
                                        <label class="summary-label d-block mb-1">Booked By</label>
                                        <AsyncSelect
                                            placeholder="Select staff"
                                            v-model="bookingInfo.booked_by"
                                            :api-service="fetchContactProfiles"
                                            :additional-query="{ type: 'employee' }"
                                            :format-label="formatPatientLabel"
                                        />
                                    </div>
                                    <div class="col-12 col-sm-6">
                                        <label class="summary-label d-block mb-1">Referrer</label>
                                        <AsyncSelect
                                            placeholder="Select referrer"
                                            v-model="bookingInfo.referrer"
                                            :api-service="fetchContactProfiles"
                                            :additional-query="{ type: 'agent' }"
                                            :format-label="formatPatientLabel"
                                        />
                                    </div>
                                    <div class="col-12">
                                        <label class="summary-label d-block mb-1">Notes</label>
                                        <textarea v-model="bookingInfo.notes" rows="2" class="form-control" placeholder="Notes"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <RoomSummaryCard
                        v-model="selectedRooms"
                        :nights="nights"
                        :check-in="reservation.check_in"
                        :check-out="reservation.check_out"
                        :saving="savingRooms"
                        @onSelectRooms="openCheckinDetailsModal"
                        @onSave="saveCheckinDetails"
                    />

                    <AdditionalGuestsCard
                        :guests="additionalGuests"
                        :primary-guest-id="reservation.guest_id"
                        @onAddGuest="openGuestModal"
                        @onLinkGuest="linkGuest"
                        @onRemoveGuest="onRemoveGuest"
                    />
                </div>

                <div class="col-12 col-md-5 mb-2">
                    <HotelBillPanel
                        :ipd-register-id="ipdRegisterId"
                        :guest-id="reservation.guest_id"
                        :rooms="selectedRooms"
                        :nights="nights"
                        :advance-receipts="advanceReceipts"
                        @onBillSaved="onBillSaved"
                    />
                </div>
            </div>
        </div>

        <Teleport to="body">
            <GuestModal
                v-if="showGuestModal"
                @onCreateProfile="onGuestCreated"
                @onClose="closeGuestModal"
            />

            <CheckinDetailsModal
                v-if="showCheckinDetailsModal"
                v-model="selectedRooms"
                :reservation="reservation"
                :own-room-ids="ownRoomIds"
                :nights="nights"
                @onClose="closeCheckinDetailsModal"
            />
        </Teleport>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import GuestModal from '@/components/molecule/company/hotel/hotel-checkin/GuestModal.vue';
import GuestSummary from '@/components/molecule/company/hotel/hotel-checkin/GuestSummary.vue';
import CheckinDetailsModal from '@/components/molecule/company/hotel/hotel-checkin/CheckinDetailsModal.vue';
import RoomSummaryCard from '@/components/molecule/company/hotel/hotel-checkin/RoomSummaryCard.vue';
import AdditionalGuestsCard from '@/components/molecule/company/hotel/hotel-checkin/AdditionalGuestsCard.vue';
import HotelBillPanel from '@/components/molecule/company/hotel/hotel-checkin/HotelBillPanel.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const $route = useRoute();
const $router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {
    fetchHotelCheckin,
    addHotelCheckinGuest,
    removeHotelCheckinGuest,
    fetchSingleHotelReservation,
    updateHotelReservation,
} = handleHotel();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();

const loading = ref(false);
const savingRooms = ref(false);
const reservation = ref(null);
const additionalGuests = ref([]);
const selectedRooms = ref([]);
const ownRoomIds = ref([]);
const advanceReceipts = ref([]);
const showGuestModal = ref(false);
const showCheckinDetailsModal = ref(false);
const bookingInfo = ref({
    booked_by: null,
    referrer: null,
    notes: '',
});

const ipdRegisterId = computed(() => $route.params.ipdRegisterId);

const nights = computed(() => {
    if (! reservation.value) return 1;
    const checkIn = new Date(reservation.value.check_in);
    const checkOut = new Date(reservation.value.check_out);
    const diff = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 1;
    return diff;
});

const primaryGuest = computed(() => {
    if (! reservation.value) return {};
    return {
        guest_id: reservation.value.guest_id,
        guest_name: reservation.value.guest_name,
        guest_mobile: reservation.value.guest_mobile,
        guest_birthday: reservation.value.guest_birthday,
        guest_gender: reservation.value.guest_gender,
        guest_nid: reservation.value.guest_nid,
        guest_present_address: reservation.value.guest_present_address,
    };
});

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const goBack = () => {
    const redirectRoute = $route.query.redirect_route;
    const routeQuery = {};
    if ($route.query.tab) routeQuery.tab = $route.query.tab;
    $router.push({
        name: redirectRoute || 'hotel-reservation',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: routeQuery,
    });
};

const loadCheckin = async () => {
    loading.value = true;
    await fetchHotelCheckin(ipdRegisterId.value, getBaseQuery()).then(res => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        reservation.value = res.data;
        additionalGuests.value = res.data.guests;
    }).finally(() => {
        loading.value = false;
    });
};

const loadReservationRooms = async () => {
    await fetchSingleHotelReservation(getBaseQuery(), ipdRegisterId.value).then(res => {
        if (! res.status) return;
        selectedRooms.value = res.data.rooms.map(room => {
            return {
                service_resource_id: room.service_resource_id,
                name: room.name,
                rate: room.rate,
            };
        });
        ownRoomIds.value = res.data.rooms.map(room => room.service_resource_id);
        advanceReceipts.value = resolveAdvanceReceipts(res.data.payments);
        bookingInfo.value = {
            booked_by: res.data.booked_by,
            referrer: res.data.referrer,
            notes: res.data.notes,
        };
    });
};

const resolveAdvanceReceipts = (payments) => {
    if (! payments) return [];
    return payments.filter(payment => {
        if (payment.receipt_type !== 'advance') return false;
        return Number(payment.available_amount) > 0;
    });
};

const openGuestModal = () => {
    showGuestModal.value = true;
};

const closeGuestModal = () => {
    showGuestModal.value = false;
};

const openCheckinDetailsModal = () => {
    showCheckinDetailsModal.value = true;
};

const closeCheckinDetailsModal = () => {
    showCheckinDetailsModal.value = false;
};

const linkGuest = async (contactProfileId) => {
    loading.value = true;
    const payload = {
        company_id: $route.params.companyId,
        contact_profile_id: contactProfileId,
    };
    await addHotelCheckinGuest(ipdRegisterId.value, payload).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        await loadCheckin();
    }).finally(() => {
        loading.value = false;
    });
};

const onGuestCreated = async (profile) => {
    closeGuestModal();
    await linkGuest(profile.id);
};

const onRemoveGuest = async (guest) => {
    loading.value = true;
    await removeHotelCheckinGuest(ipdRegisterId.value, guest.guest_id, getBaseQuery()).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        await loadCheckin();
    }).finally(() => {
        loading.value = false;
    });
};

const resolveContactId = (contact) => {
    if (! contact) return null;
    return contact.id;
};

const buildRoomsPayload = () => {
    return {
        company_id: $route.params.companyId,
        contact_profile_id: reservation.value.guest_id,
        registration_status: 'checked_in',
        check_in: reservation.value.check_in,
        check_out: reservation.value.check_out,
        rooms: selectedRooms.value,
        admitted_by: resolveContactId(bookingInfo.value.booked_by),
        referrer_id: resolveContactId(bookingInfo.value.referrer),
        notes: bookingInfo.value.notes,
    };
};

const saveCheckinDetails = async () => {
    savingRooms.value = true;
    await updateHotelReservation(buildRoomsPayload(), ipdRegisterId.value).then(res => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        ownRoomIds.value = selectedRooms.value.map(room => room.service_resource_id);
        reservation.value.registration_status = 'checked_in';
    }).finally(() => {
        savingRooms.value = false;
    });
};

const onBillSaved = async () => {
    if (! reservation.value) return;
    await updateHotelReservation(buildRoomsPayload(), ipdRegisterId.value).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        ownRoomIds.value = selectedRooms.value.map(room => room.service_resource_id);
        reservation.value.registration_status = 'checked_in';
        await loadReservationRooms();
    });
};

onMounted(async () => {
    await Promise.all([
        loadCheckin(),
        loadReservationRooms(),
    ]);
});
</script>

<style>
.section-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}
.section-card-header {
    padding: 0.6rem 0.9rem;
    background: #f8f9fb;
    border-bottom: 1px solid #eef0f3;
}
.section-card-body {
    padding: 0.9rem;
}
</style>

<style scoped>
.checkin-card {
    min-height: 75vh;
    padding-bottom: 2rem;
}
.guest-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.4rem 0.6rem;
    font-size: 0.82rem;
}
.checkin-dates {
    font-size: 0.85rem;
    color: #1f2937;
}
.checkin-dates .summary-label {
    font-weight: 600;
    color: #6b7280;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-right: 0.3rem;
}
.booking-info {
    border-top: 1px solid #eef0f3;
    padding-top: 0.75rem;
}
.booking-info .summary-label {
    font-weight: 600;
    color: #6b7280;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
</style>
